// ─────────────────────────────────────────────
//  useFacilities — live list of all facilities for the setup area.
//
//  Source of truth is the Firestore `facilities` collection, merged with the
//  KNOWN_FACILITIES seed so the core AdventHealth facilities always appear in
//  the dropdown even before their doc has been written. Custom facilities added
//  via "Add Facility" show up purely from Firestore. Each entry carries:
//    id, name, label, isActive, core (true for the seeded AdventHealth three —
//    those cannot be removed).
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { listenToFacilities } from "../firebase/firestore";
import { KNOWN_FACILITIES } from "../domain/facilities";
import { isSandboxBoot } from "../sandbox/mode";

export function useFacilities() {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const unsub = listenToFacilities(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setDocs([])
    );
    return unsub;
  }, []);

  const byId = new Map();
  // Seed the core facilities first (default = active; the other two are
  // inactive empty shells until configured).
  //
  // NOT in the sandbox. KNOWN_FACILITIES is this deployment's seed —
  // "AdventHealth Wesley Chapel", "AdventHealth Carrollwood", "AdventHealth
  // Tampa". A stranger evaluating the app on their own phone has exactly one
  // facility, the one the wizard just created, and seeding a real hospital's
  // campus names into their dropdowns is both confusing and a tenant leak.
  // Caught live: the first-login facility prompt offered "AdventHealth Wesley
  // Chapel" inside a fresh sandbox. Live docs only there.
  if (!isSandboxBoot()) {
    KNOWN_FACILITIES.forEach((f) =>
      byId.set(f.id, { id: f.id, name: f.label, label: f.label, isActive: f.id === "default", core: true })
    );
  }
  // Overlay live docs — their saved name/isActive win; new ids are appended.
  docs.forEach((d) => {
    const prev = byId.get(d.id);
    byId.set(d.id, {
      ...prev,
      ...d,
      name: d.name || prev?.name || d.id,
      label: d.name || prev?.label || d.id,
      core: prev?.core === true,
    });
  });

  return [...byId.values()];
}
